// @ts-check
import React, { Component } from 'react';
import { Page, Row, TabbedWidget } from 'shared/containers/portal_layout';
import TabPane from 'shared/components/TabPane';
import ReferralTabs from './ReferralTabs';
import SearchReferrals from './SearchReferrals';
import ReceivedReferralsTable from './ReceivedReferralsTable';
import SentReferralsTable from './SentReferralsTable';
import actions from './actions';
import LoadingContent from 'shared/components/LoadingContent';
import uid from 'uid';
import api from 'utils/api';
import { connect } from 'react-redux';
import { ReceivedFilterPanel } from './ReceivedFilterPanel';

const primaryTabMappings = {
  sent: 'Sent Referrals',
  received: 'Received Referrals',
};

const tabIds = {
  received: {
    pending: 'pendingReceived',
    confirmed: 'confirmedReceived',
    rejected: 'rejectedEtcReceived',
    all: 'allReceived',
    search: 'searchReceived',
  },
  sent: {
    pending: 'pendingSent',
    scheduled: 'scheduledSent',
    rejected: 'rejectedEtcSent',
    all: 'allSent',
    search: 'searchSent',
  },
};

const { received, sent } = tabIds;

/** @type {import("./ReceivedFilterPanel/ReceivedFilterPanel").ReceivedFilter} */
const initialReceivedFilter = {
  assignedAgentId: null,
  locationId: null,
  providerIds: null,
  specialtyId: null,
  referralTagId: null,
};

const initialSentFilter = {};

/**
 * Upper-cases the first letter of a string.
 * "example" -> "Example"
 * @param {string} str
 * @returns {string}
 */
function upperCaseFirst(str) {
  if (str === '' || str.length < 1) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

class IndexPage extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      fetched: false,
      receivedFilter: initialReceivedFilter,
      sentFilter: initialSentFilter,
      tabs: {
        pendingReceived: {
          name: 'Pending',
          pathname: '/a/portal/referrals',
          path: '/agent_portal/api/received_referrals?type=pending_received',
          currentPage: 1,
          emptyMessage: 'You do not have any pending referrals at this time',
          showCount: true,
          fetching: false,
        },
        confirmedReceived: {
          name: 'Scheduled',
          pathname: '/a/portal/referrals/received/confirmed',
          path: '/agent_portal/api/received_referrals?type=confirmed',
          currentPage: 1,
          emptyMessage: 'You do not have any scheduled referrals at this time',
          fetching: false,
        },
        rejectedEtcReceived: {
          name: 'Rejected',
          pathname: '/a/portal/referrals/received/rejected',
          path: '/agent_portal/api/received_referrals?type=rejected_etc',
          currentPage: 1,
          emptyMessage: 'You do not have any rejected referrals at this time',
          fetching: false,
        },
        allReceived: {
          name: 'All',
          pathname: '/a/portal/referrals/received/all',
          path: '/agent_portal/api/received_referrals?type=all',
          currentPage: 1,
          emptyMessage: 'You have not received any referrals on AristaMD yet',
          fetching: false,
        },
        searchReceived: {
          name: 'Search',
          pathname: '/a/portal/referrals/received/search',
          path: '/agent_portal/api/referrals/search?direction=received',
          currentPage: 1,
          searchTerm: '',
          search: true,
          fetching: false,
        },
        pendingSent: {
          name: 'Pending',
          pathname: '/a/portal/referrals/sent',
          path: '/agent_portal/api/sent_referrals?type=pending_sent',
          currentPage: 1,
          emptyMessage:
            'You do not have any pending sent referrals at this time',
          fetching: false,
        },
        scheduledSent: {
          name: 'Scheduled',
          pathname: '/a/portal/referrals/sent/scheduled',
          path: '/agent_portal/api/sent_referrals?type=scheduled',
          currentPage: 1,
          emptyMessage:
            'You do not have any scheduled sent referrals at this time',
          fetching: false,
        },
        rejectedEtcSent: {
          name: 'Rejected',
          pathname: '/a/portal/referrals/sent/rejected',
          path: '/agent_portal/api/sent_referrals?type=rejected_etc',
          currentPage: 1,
          emptyMessage:
            'You do not have any rejected sent referrals at this time',
          fetching: false,
        },
        allSent: {
          name: 'All',
          pathname: '/a/portal/referrals/sent/all',
          path: '/agent_portal/api/sent_referrals?type=all',
          currentPage: 1,
          emptyMessage: 'You have not sent any referrals on AristaMD yet',
          fetching: false,
        },
        searchSent: {
          name: 'Search',
          pathname: '/a/portal/referrals/sent/search',
          path: '/agent_portal/api/referrals/search?direction=sent',
          currentPage: 1,
          searchTerm: '',
          search: true,
        },
      },
    };

    this.refresh = this.refresh.bind(this);
    this.fetchSingleTab = this.fetchSingleTab.bind(this);
    this.getCurrentTabId = this.getCurrentTabId.bind(this);
    this.fetchCurrentTab = this.fetchCurrentTab.bind(this);
    this.fetchAllTabs = this.fetchAllTabs.bind(this);
    this.updateReceivedFilter = this.updateReceivedFilter.bind(this);
    this.clearReceivedFilter = this.clearReceivedFilter.bind(this);
    this.updateSentFilter = this.updateSentFilter.bind(this);
    this.clearSentFilter = this.clearSentFilter.bind(this);
    this.resetFetched = this.resetFetched.bind(this);
  }

  componentDidMount() {
    this.refresh();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.mostRecentReferralReceivedAt !==
      this.props.mostRecentReferralReceivedAt
    ) {
      this.refresh();
    } else if (prevProps.location !== this.props.location) {
      this.fetchCurrentTab();
    }
  }

  // Find the current tab id by pathname
  getCurrentTabId() {
    const tabs = this.state.tabs;
    return Object.keys(tabs).find((tabId) =>
        tabs[tabId].pathname === this.props.location.pathname
    );
  }

  fetchCurrentTab() {
    const tabs = this.state.tabs;
    const tabId = this.getCurrentTabId();
    const tab = tabs[tabId];
    // Fetch the tab if we haven't fetched it already AND it is not a search tab or a search term was entered
    if (!tab.fetched && (!tabId.startsWith('search') || tab.searchTerm)) {
      const desiredPage = tab.currentPage || 1;
      const searchTerm = tab.searchTerm;
      this.fetchSingleTab(tabId, desiredPage, searchTerm);
    }
  }

  fetchAllTabs() {
    const { sentFilter, receivedFilter } = this.state;
    const fetchParams = {};

    /** Tab PageNum & SearchTerm */
    Object.keys(this.state.tabs).forEach((tabId) => {
      const tab = this.state.tabs[tabId];
      fetchParams[`${tabId}PageNum`] = tab.currentPage;
      fetchParams[`${tabId}SearchTerm`] = tab.searchTerm;
    });

    /** Received Filters */
    Object.keys(receivedFilter).forEach((key) => {
      if (receivedFilter[key] !== null) {
        fetchParams[`received${upperCaseFirst(key)}`] = receivedFilter[key];
      }
    });

    /** Sent Filters */
    Object.keys(sentFilter).forEach((key) => {
      if (sentFilter[key] !== null) {
        fetchParams[`sent${upperCaseFirst(key)}`] = sentFilter[key];
      }
    });

    return actions.fetchReferrals(fetchParams).then((data) => {
      const newTabs = {};
      Object.keys(this.state.tabs).forEach((tabId) => {
        newTabs[tabId] = { ...this.state.tabs[tabId], ...data[tabId] };
      });
      this.setState({
        tabs: newTabs,
        fetched: true,
      });

      actions.updatePendingReferralsCount(newTabs.pendingReceived.total);
    });
  }

  /**
   * This function can be called whenever data changes or user reloaded page
   */
  async refresh() {
    await this.resetFetched();
    this.fetchCurrentTab();
    const tabId = this.getCurrentTabId();
    const tabs = this.state.tabs;
    if (tabId !== 'pendingReceived') {
      // Also fetch Pending Received tab in order to update the pending count
      await this.fetchSingleTab('pendingReceived', tabs['pendingReceived'].currentPage || 1);
    }
  }

  /**
   * Reset the fetched flag of all tabs so the tab will
   * load fresh data the next time fetchCurrentTab is called
   */
  resetFetched() {
    const tabs = {...this.state.tabs};
    Object.keys(tabs).forEach((tabId) => {
      const tab = this.state.tabs[tabId];
      tabs[tabId] = {...tab, fetched: false};
    });
    return new Promise((resolve) => {
      this.setState({
        tabs: {...tabs},
      }, resolve);
    });
  }

  updateReceivedFilter(filterValues) {
    this.setState((state) => {
      const prevFilter = state.receivedFilter;
      return {
        receivedFilter: {
          ...prevFilter,
          ...filterValues,
        },
      };
    }, this.refresh);
  }

  clearReceivedFilter() {
    this.setState({ receivedFilter: initialReceivedFilter }, this.refresh);
  }

  updateSentFilter(filterValues) {
    this.setState((state) => {
      const prevFilter = state.sentFilter;
      return {
        sentFilter: {
          ...prevFilter,
          ...filterValues,
        },
      };
    }, this.refresh);
  }

  clearSentFilter() {
    this.setState({ sentFilter: initialSentFilter }, this.refresh);
  }

  fetchSingleTab(tabId, desiredPage = 1, searchTerm) {
    let tab = this.state.tabs[tabId];
    const requestId = uid();
    this.setState({
      tabs: {
        ...this.state.tabs,
        [tabId]: { ...tab, requestId, fetching: true },
      },
    });

    let params = {
      page: desiredPage,
      search_term: searchTerm,
    };

    const { sentFilter, receivedFilter } = this.state;

    /** Received Filters */
    Object.keys(receivedFilter).forEach((key) => {
      if (receivedFilter[key] !== null) {
        params[`received${upperCaseFirst(key)}`] = receivedFilter[key];
      }
    });

    /** Sent Filters */
    Object.keys(sentFilter).forEach((key) => {
      if (sentFilter[key] !== null) {
        params[`sent${upperCaseFirst(key)}`] = sentFilter[key];
      }
    });

    return api.get(tab.path, { params }).then((response) => {
      tab = this.state.tabs[tabId];
      if (tab.requestId === requestId) {
        const newTab = {
          ...tab,
          currentPage: desiredPage,
          searchTerm,
          referrals: response.data.referrals,
          numPages: response.data.numPages,
          total: response.data.total,
          fetching: false,
          fetched: true
        };
        this.setState({
          tabs: {
            ...this.state.tabs,
            [tabId]: newTab,
          },
          fetched: true,
        });

        if (tabId === 'pendingReceived') {
          actions.updatePendingReferralsCount(newTab.total);
        }
      }
    });
  }

  render() {
    if (!this.state.fetched) return <LoadingContent />;
    return (
      <PresentationComponent
        {...this.state}
        {...this.props}
        refresh={this.refresh}
        fetchSingleTab={this.fetchSingleTab}
        updateReceivedFilter={this.updateReceivedFilter}
        clearReceivedFilter={this.clearReceivedFilter}
        updateSentFilter={this.updateSentFilter}
        clearSentFilter={this.clearSentFilter}
      />
    );
  }
}

const mapStateToProps = (state) => {
  const mostRecentReferral =
    state.newReceivedReferrals[state.newReceivedReferrals.length - 1];
  return {
    mostRecentReferralReceivedAt: mostRecentReferral
      ? mostRecentReferral.receivedAt
      : null,
  };
};
export default connect(mapStateToProps)(IndexPage);

const PresentationComponent = (props) => {
  /* eslint-disable no-unused-vars */
  const {
    tabs,
    refresh,
    fetchSingleTab,
    receivedFilter,
    sentFilter,
    updateReceivedFilter,
    updateSentFilter,
    clearReceivedFilter,
    clearSentFilter,
  } = props;
  /* eslint-enable no-unused-vars */
  const { primaryTab, secondaryTab } = props.route;

  const fetch = (tabId) => (...rest) => fetchSingleTab(tabId, ...rest);

  return (
    <Page title="My Referrals: Sent and Received">
      <Row class>
        <TabbedWidget
          span={12}
          className="referrals-index"
          activeTabName={primaryTabMappings[primaryTab]}
        >
          <TabPane
            tabName="Received Referrals"
            linkTo={tabs.pendingReceived.pathname}
          >
            <div style={{ marginBottom: '1rem' }}>
              <ReceivedFilterPanel
                initialValue={initialReceivedFilter}
                appliedValue={receivedFilter}
                onApply={updateReceivedFilter}
                onClear={clearReceivedFilter}
              />
            </div>
            <ReferralTabs
              activeTabName={
                primaryTab === 'received'
                  ? tabs[received[secondaryTab]].name
                  : null
              }
            >
              {[
                'pendingReceived',
                'confirmedReceived',
                'rejectedEtcReceived',
                'allReceived',
                'searchReceived',
              ].map((tabId) => {
                const tab = tabs[tabId];
                return (
                  <TabPane
                    tabName={tab.name}
                    linkTo={tab.pathname}
                    key={tab.name}
                    extraNavContent={
                      tab.showCount ? (
                        <span className="label">{tab.total}</span>
                      ) : null
                    }
                  >
                    {tab.search ? (
                      <SearchReferrals
                        refresh={refresh}
                        fetch={fetch(tabId)}
                        {...tab}
                        tableComponent={ReceivedReferralsTable}
                      />
                    ) : (
                      <ReceivedReferralsTable
                        fetch={fetch(tabId)}
                        refresh={refresh}
                        {...tab}
                      />
                    )}
                  </TabPane>
                );
              })}
            </ReferralTabs>
          </TabPane>
          <TabPane tabName="Sent Referrals" linkTo={tabs.pendingSent.pathname}>
            <ReferralTabs
              activeTabName={
                primaryTab === 'sent' ? tabs[sent[secondaryTab]].name : null
              }
            >
              {[
                'pendingSent',
                'scheduledSent',
                'rejectedEtcSent',
                'allSent',
                'searchSent',
              ].map((tabId) => {
                const tab = tabs[tabId];
                return (
                  <TabPane
                    tabName={tab.name}
                    linkTo={tab.pathname}
                    key={tab.name}
                  >
                    {tab.search ? (
                      <SearchReferrals
                        refresh={refresh}
                        fetch={fetch(tabId)}
                        {...tab}
                        tableComponent={SentReferralsTable}
                      />
                    ) : (
                      <SentReferralsTable
                        fetch={fetch(tabId)}
                        refresh={refresh}
                        {...tab}
                      />
                    )}
                  </TabPane>
                );
              })}
            </ReferralTabs>
          </TabPane>
        </TabbedWidget>
      </Row>
    </Page>
  );
};
